import { VaultChartSeriesKey } from '@/app/shared/constants/vaults';
import { formatAxisDate, parseNumber } from '@/app/shared/lib/formatters';
import { isTotalPerformanceTranche, resolveVaultChartSeriesKey } from '@/app/shared/lib/tranche';
import { VaultPerformancePoint, VaultPerformanceTranche } from '@/app/shared/services/api/types';
import { PerformanceChartData } from '@/app/shared/ui/PerformanceChart/types';

export interface VaultChartPoint {
  date: string;
  tooltipLabel: string;
  base: number;
  senior: number | null;
  junior: number | null;
}

const parseMetricValue = (point: VaultPerformancePoint, metric: PerformanceChartData): number | null => {
  const raw =
    metric === PerformanceChartData.TVL
      ? point.tvl
      : metric === PerformanceChartData.SHARE_PRICE
        ? point.sharePrice
        : point.apr;

  const value = parseNumber(raw);
  if (value == null) return null;
  if (metric === PerformanceChartData.APR) return value * 100;
  return value;
};

const parseBaseApr = (point: VaultPerformancePoint): number => {
  const value = parseNumber(point.baseApr) ?? 0;
  return value * 100;
};

const emptyChartPoint = (date: string): VaultChartPoint => ({
  date: formatAxisDate(date),
  tooltipLabel: formatAxisDate(date),
  base: 0,
  senior: null,
  junior: null,
});

const SHARE_PRICE_Y_MIN = 0.95;
const SHARE_PRICE_Y_MAX = 1.1;

export const getPerformanceScale = (values: number[], metric: PerformanceChartData) => {
  if (metric === PerformanceChartData.SHARE_PRICE) {
    if (values.length === 0) {
      return { min: SHARE_PRICE_Y_MIN, max: SHARE_PRICE_Y_MAX };
    }

    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);

    // Default window 0.95–1.1; expand automatically if price leaves that range.
    let min = Math.min(SHARE_PRICE_Y_MIN, dataMin);
    let max = Math.max(SHARE_PRICE_Y_MAX, dataMax);

    if (dataMin < SHARE_PRICE_Y_MIN || dataMax > SHARE_PRICE_Y_MAX) {
      const padding = Math.max(0.005, (max - min) * 0.05);
      if (dataMin < SHARE_PRICE_Y_MIN) min = Math.max(0, dataMin - padding);
      if (dataMax > SHARE_PRICE_Y_MAX) max = dataMax + padding;
    }

    return {
      min: Number(min.toFixed(3)),
      max: Number(max.toFixed(3)),
    };
  }

  const rawMaximum = Math.max(...values, 0);
  const step = 5;
  return { min: 0, max: Math.max(step, Math.ceil((rawMaximum * 1.1) / step) * step) };
};

export const mapPerformanceToChart = (
  tranches: VaultPerformanceTranche[],
  metric: PerformanceChartData,
): { points: VaultChartPoint[]; series: VaultChartSeriesKey[] } => {
  const totalTranche = tranches.find((tranche) => isTotalPerformanceTranche(tranche.name, tranche.symbol));
  const keyed = tranches
    .map((tranche) => ({ key: resolveVaultChartSeriesKey(tranche.name, tranche.symbol), tranche }))
    .filter((item): item is { key: VaultChartSeriesKey; tranche: VaultPerformanceTranche } => item.key != null);

  const series = Array.from(new Set(keyed.map((item) => item.key)));
  const dates = new Map<string, VaultChartPoint>();

  keyed.forEach(({ key, tranche }) => {
    tranche.points.forEach((point) => {
      const existing = dates.get(point.date) ?? emptyChartPoint(point.date);
      if (
        metric !== PerformanceChartData.SHARE_PRICE &&
        !totalTranche &&
        existing.base === 0
      ) {
        existing.base = parseBaseApr(point);
      }
      existing[key] = parseMetricValue(point, metric);
      dates.set(point.date, existing);
    });
  });

  totalTranche?.points.forEach((point) => {
    const existing = dates.get(point.date) ?? emptyChartPoint(point.date);
    if (metric === PerformanceChartData.TVL) {
      existing.base = parseMetricValue(point, metric) ?? existing.base;
    } else if (metric === PerformanceChartData.APR) {
      existing.base = parseBaseApr(point);
    }
    dates.set(point.date, existing);
  });

  const points = Array.from(dates.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, point]) => {
      if (metric === PerformanceChartData.TVL && !totalTranche) {
        return {
          ...point,
          base: (point.senior ?? 0) + (point.junior ?? 0),
        };
      }
      return point;
    });

  return { points, series };
};

export const getLatestChartTvl = (
  points: VaultChartPoint[],
): { total: number; senior: number | null; junior: number | null } | null => {
  const latest = points[points.length - 1];
  if (!latest) return null;

  const hasTrancheTvl = latest.senior != null || latest.junior != null;
  if (!hasTrancheTvl && latest.base === 0) return null;

  return {
    total: latest.base,
    senior: latest.senior,
    junior: latest.junior,
  };
};

export const getCurrentPerformanceTvl = (
  tranches: VaultPerformanceTranche[],
): { total: number; senior: number | null; junior: number | null } | null => {
  const keyed = tranches
    .map((tranche) => ({
      key: resolveVaultChartSeriesKey(tranche.name, tranche.symbol),
      tranche,
    }))
    .filter(
      (item): item is { key: VaultChartSeriesKey; tranche: VaultPerformanceTranche } =>
        item.key != null,
    );

  let senior: number | null = null;
  let junior: number | null = null;

  for (const { key, tranche } of keyed) {
    const raw = tranche.current?.tvlUsd ?? tranche.current?.tvl;
    const value = raw != null ? parseNumber(raw) : null;
    if (value == null) continue;
    if (key === 'senior') senior = value;
    if (key === 'junior') junior = value;
  }

  if (senior == null && junior == null) return null;

  const totalTranche = tranches.find((tranche) =>
    isTotalPerformanceTranche(tranche.name, tranche.symbol),
  );
  const totalFromApi = totalTranche?.current
    ? parseNumber(totalTranche.current.tvlUsd ?? totalTranche.current.tvl)
    : null;

  return {
    total: totalFromApi ?? (senior ?? 0) + (junior ?? 0),
    senior,
    junior,
  };
};
